import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { AddToCart } from "@/components/AddToCart";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";

const T = {
  fr: {
    notFound: "Référence introuvable.", back: "Retour", backTop: "← Maroquinerie",
    kicker: (roman) => `Maroquinerie ${roman}`,
    seoTitle: (p) => `${p.name} — Maroquinerie ${p.roman} · Maison Partita`,
    seoDesc: (p, m) => `${p.name}, sac à main. ${m}`,
    altBag: (p, m) => `Sac ${p.name} — Maroquinerie ${p.roman}, ${m}`,
    format: "Cuir · Fait main · Pièce numérotée",
    material: "Matière",
    atelier: "Atelier : non communiqué.",
    rrp: "Prix de vente conseillé", enquire: "Nous écrire",
  },
  en: {
    notFound: "Reference not found.", back: "Back", backTop: "← Leather Goods",
    kicker: (roman) => `Leather Goods ${roman}`,
    seoTitle: (p) => `${p.name} — Leather Goods ${p.roman} · Maison Partita`,
    seoDesc: (p, m) => `${p.name}, handbag. ${m}`,
    altBag: (p, m) => `${p.name} bag — Leather Goods ${p.roman}, ${m}`,
    format: "Leather · Handmade · Numbered piece",
    material: "Material",
    atelier: "Workshop: undisclosed.",
    rrp: "Recommended retail price", enquire: "Write to us",
  },
};

export default function MaroquinerieProduct() {
  const { slug } = useParams();
  const lang = useLang();
  const t = T[lang];
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null); setErr(false);
    api.get(`/products/maroquinerie-${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  const material = p && loc(p, lang, "material");
  const description = p && loc(p, lang, "description");

  useSeo(
    p ? t.seoTitle(p) : "Maroquinerie · Maison Partita",
    p ? t.seoDesc(p, material) : ""
  );

  useEffect(() => {
    if (!p) return () => {};
    const ld = {
      "@context": "https://schema.org", "@type": "Product",
      name: `${p.name} — Maroquinerie ${p.roman}`, description: p.description,
      brand: { "@type": "Brand", name: "Maison Partita" },
      offers: { "@type": "Offer", priceCurrency: "EUR", price: p.price, availability: "https://schema.org/InStock" },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, [p]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-40 text-center text-zinc-400">{t.notFound} <Link to={lp(lang, "/maroquinerie")} className="val-link underline">{t.back}</Link></div>;
  if (!p) return <div className="py-40 text-center text-zinc-600 tracking-[0.2em] uppercase text-[13px]">…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-16 pb-24">
      <Link to={lp(lang, "/maroquinerie")} className="val-link text-[13px] tracking-[0.2em] uppercase" data-testid="back-link">{t.backTop}</Link>

      <div className="mt-12 grid gap-16 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden bg-zinc-900 border border-zinc-800">
            <img src={p.photo} alt={t.altBag(p, material)}
              width="848" height="1264" decoding="async"
              className="w-full h-auto" style={{ aspectRatio: "4/5", objectFit: "cover" }} />
          </div>
        </Reveal>

        <div className="lg:pt-6">
          <Reveal>
            <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.kicker(p.roman)}</p>
            <h1 className="mt-3 text-4xl sm:text-5xl tracking-[0.04em] text-zinc-50">{p.name}</h1>

            <p className="mt-8 text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.format}</p>

            <p className="mt-10 text-[19px] leading-[1.85] text-zinc-300">{description}</p>

            <div className="mt-10 space-y-3">
              <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.material}</p>
              <p className="text-zinc-300 leading-relaxed">{material}</p>
            </div>

            <p className="mt-8 text-zinc-500">{t.atelier}</p>

            <div className="mt-12 border-t border-zinc-900 pt-10" data-testid="product-available">
              <p className="text-3xl text-zinc-100">{p.price} €</p>
              <p className="mt-2 text-[13px] tracking-[0.16em] uppercase text-zinc-600">{t.rrp}</p>
              <AddToCart className="mt-8" product={{ reference: p.reference, label: `${p.name} · ${p.roman}`, collection: lang === "en" ? "Leather goods" : "Maroquinerie", price: p.price, url: `/maroquinerie/${p.slug}` }} />
              <Link to={lp(lang, "/contact")} className="val-link text-[13px] tracking-[0.2em] uppercase mt-6 inline-block" data-testid="product-enquire">{t.enquire}</Link>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
